import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/layout/Container';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { Progress } from '@/components/ui/Progress';
import { NativeSelect } from '@/components/ui/NativeSelect';
import {
  FileText,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Target,
  Calendar,
  BarChart,
  Lightbulb,
  Download,
  RefreshCw,
  ChevronRight
} from 'lucide-react';
import { aspirationsService } from '@/services/aspirationsService';
import { goalsService } from '@/services/goalsService';
import { listsService } from '@/services/listsService';
import { universalCompletionService } from '@/services/universalCompletionService';
import { aiService } from '@/services/aiService';
import { AIAspirationReport, AIReportRequest } from '@/types/ai';
import { Aspiration } from '@/types/aspirations';
import { LoadingState } from '@/components/ui/Loading';

export function Reports() {
  const navigate = useNavigate();
  const [aspirations, setAspirations] = useState<Aspiration[]>([]);
  const [selectedAspirationId, setSelectedAspirationId] = useState<string>('');
  const [report, setReport] = useState<AIAspirationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAspirations();
  }, []);

  const loadAspirations = async () => {
    try {
      const data = await aspirationsService.getAspirations();
      setAspirations(data);
      if (data.length > 0 && !selectedAspirationId) {
        setSelectedAspirationId(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load aspirations:', error);
      setError('Failed to load aspirations');
    }
  };

  const generateReport = async () => {
    if (!selectedAspirationId) {
      setError('Please select an aspiration');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check AI configuration
      if (!aiService.isConfigured()) {
        setError('AI service not configured. Please add your Anthropic API key in Settings.');
        navigate('/settings');
        return;
      }

      // Gather data
      const aspiration = aspirations.find(a => a.id === selectedAspirationId);
      if (!aspiration) throw new Error('Aspiration not found');

      const goals = goalsService.getGoalsByAspirationId(selectedAspirationId);
      const allLists = listsService.getAllLists();

      // Filter lists that belong to this aspiration's goals
      const goalIds = goals.map(g => g.id);
      const relevantLists = allLists.filter(list => list.goalId && goalIds.includes(list.goalId));

      // Get completion data for today
      const today = new Date();

      // Prepare request
      const request: AIReportRequest = {
        aspiration: {
          id: aspiration.id,
          title: aspiration.title,
          description: aspiration.description,
          category: aspiration.category,
          priority: aspiration.priority,
          status: aspiration.status,
          createdAt: typeof aspiration.createdAt === 'string'
            ? aspiration.createdAt
            : aspiration.createdAt.toISOString()
        },
        goals: goals.map(goal => ({
          id: goal.id,
          title: goal.title,
          description: goal.description,
          smartCriteria: goal.smartCriteria,
          deadline: goal.deadline
            ? (typeof goal.deadline === 'string' ? goal.deadline : goal.deadline.toISOString())
            : undefined,
          progress: goal.progress
        })),
        lists: relevantLists.map(list => {
          const completionData = universalCompletionService.getListDataForContext(list, today);
          return {
            id: list.id,
            goalId: list.goalId || '',
            title: list.title,
            description: list.description,
            status: list.status,
            priority: list.priority,
            items: list.items.map(item => {
              const itemWithCompletion = completionData.items.find(i => i.id === item.id);
              const isCompleted = itemWithCompletion?.completed || false;
              const completedAt = itemWithCompletion?.completedAt
                ? (typeof itemWithCompletion.completedAt === 'string'
                  ? itemWithCompletion.completedAt
                  : itemWithCompletion.completedAt.toISOString())
                : undefined;
              return {
                content: item.content,
                completed: isCompleted,
                completedAt: completedAt
              };
            })
          };
        }),
        userContext: {
          workingHours: 40, // Could be configured in settings
          preferredPace: 'moderate',
          currentDate: new Date().toISOString()
        }
      };

      // Generate report
      const generatedReport = await aiService.generateAspirationReport(request);
      setReport(generatedReport);

    } catch (error: any) {
      console.error('Report generation failed:', error);
      setError(error.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    if (!report) return;

    const dataStr = JSON.stringify(report, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `aspiration-report-${report.aspirationId}-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const renderProgressCard = () => {
    if (!report) return null;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Progress Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Overall Progress</span>
              <span className="font-medium">{report.summary.overallProgress}%</span>
            </div>
            <Progress value={report.summary.overallProgress} className="h-2" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Goals</p>
              <p className="text-2xl font-bold">
                {report.summary.completedGoals}/{report.summary.totalGoals}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Tasks</p>
              <p className="text-2xl font-bold">
                {report.summary.completedItems}/{report.summary.totalItems}
              </p>
            </div>
          </div>

          <div className="pt-2 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Est. Completion</span>
              <Badge variant="outline">
                <Calendar className="h-3 w-3 mr-1" />
                {report.summary.estimatedCompletion}
              </Badge>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-muted-foreground">Current Phase</span>
              <Badge variant={
                report.summary.currentPhase === 'completed' ? 'default' :
                report.summary.currentPhase === 'final-push' ? 'destructive' :
                'secondary'
              }>
                {report.summary.currentPhase.replace('-', ' ')}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAnalysisCard = () => {
    if (!report) return null;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart className="h-5 w-5" />
            Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-2 text-green-600">Strengths</h4>
            <ul className="space-y-1">
              {report.progressAnalysis.strengths.map((strength, idx) => (
                <li key={idx} className="text-sm flex items-start gap-2">
                  <CheckCircle className="h-3 w-3 mt-0.5 text-green-600" />
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2 text-orange-600">Areas for Improvement</h4>
            <ul className="space-y-1">
              {report.progressAnalysis.weaknesses.map((weakness, idx) => (
                <li key={idx} className="text-sm flex items-start gap-2">
                  <AlertCircle className="h-3 w-3 mt-0.5 text-orange-600" />
                  <span>{weakness}</span>
                </li>
              ))}
            </ul>
          </div>

          {report.progressAnalysis.blockers.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 text-red-600">Blockers</h4>
              <ul className="space-y-1">
                {report.progressAnalysis.blockers.map((blocker, idx) => (
                  <li key={idx} className="text-sm flex items-start gap-2">
                    <AlertCircle className="h-3 w-3 mt-0.5 text-red-600" />
                    <span>{blocker}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.progressAnalysis.opportunities.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 text-blue-600">Opportunities</h4>
              <ul className="space-y-1">
                {report.progressAnalysis.opportunities.map((opportunity, idx) => (
                  <li key={idx} className="text-sm flex items-start gap-2">
                    <Lightbulb className="h-3 w-3 mt-0.5 text-blue-600" />
                    <span>{opportunity}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderRecommendations = () => {
    if (!report || !report.recommendations) return null;

    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const sortedRecommendations = [...report.recommendations].sort(
      (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
    );

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Target className="h-5 w-5" />
          Recommendations
        </h3>

        {sortedRecommendations.map((rec, idx) => (
          <Card key={idx} className={
            rec.priority === 'high' ? 'border-red-200 bg-red-50/50' :
            rec.priority === 'medium' ? 'border-orange-200 bg-orange-50/50' :
            ''
          }>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base">{rec.title}</CardTitle>
                  <div className="flex gap-2 mt-1">
                    <Badge variant={
                      rec.priority === 'high' ? 'destructive' :
                      rec.priority === 'medium' ? 'outline' :
                      'secondary'
                    } className="text-xs">
                      {rec.priority} priority
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {rec.category}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">{rec.description}</p>

              {rec.actionItems && rec.actionItems.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-1">Action Items:</p>
                  <ul className="space-y-1">
                    {rec.actionItems.map((item, itemIdx) => (
                      <li key={itemIdx} className="text-sm flex items-start gap-2">
                        <ChevronRight className="h-3 w-3 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {rec.expectedImpact && (
                <div className="pt-2 border-t">
                  <p className="text-sm">
                    <span className="font-medium">Expected Impact:</span> {rec.expectedImpact}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderGoalsAnalysis = () => {
    if (!report || !report.goalsAnalysis) return null;

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Goals Analysis</h3>

        {report.goalsAnalysis.map((goal, idx) => (
          <Card key={idx}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{goal.goalTitle}</CardTitle>
                <div className="flex gap-2">
                  <Badge variant={
                    goal.status === 'completed' ? 'default' :
                    goal.status === 'on-track' ? 'secondary' :
                    goal.status === 'at-risk' ? 'outline' :
                    'destructive'
                  }>
                    {goal.status}
                  </Badge>
                  <Badge variant="outline">{goal.progress}%</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {goal.issues.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-600 mb-1">Issues:</p>
                  <ul className="space-y-1">
                    {goal.issues.map((issue, issueIdx) => (
                      <li key={issueIdx} className="text-sm text-muted-foreground">
                        • {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {goal.suggestions.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-blue-600 mb-1">Suggestions:</p>
                  <ul className="space-y-1">
                    {goal.suggestions.map((suggestion, suggIdx) => (
                      <li key={suggIdx} className="text-sm text-muted-foreground">
                        • {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Container>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Aspiration Reports</h1>
            <p className="text-muted-foreground mt-1">
              AI-powered analysis and recommendations for your aspirations
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/help')}
          >
            <FileText className="h-4 w-4 mr-1" />
            Help
          </Button>
        </div>

        {/* Aspiration Selector */}
        <Card>
          <CardHeader>
            <CardTitle>Select Aspiration</CardTitle>
            <CardDescription>
              Choose an aspiration to generate a comprehensive progress report
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <NativeSelect
              value={selectedAspirationId}
              onChange={(e) => setSelectedAspirationId(e.target.value)}
              disabled={loading}
            >
              <option value="">Select an aspiration...</option>
              {aspirations.map(aspiration => (
                <option key={aspiration.id} value={aspiration.id}>
                  {aspiration.title} ({aspiration.category})
                </option>
              ))}
            </NativeSelect>

            <div className="flex gap-2">
              <Button
                onClick={generateReport}
                disabled={!selectedAspirationId || loading}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating Report...
                  </>
                ) : (
                  <>
                    <BarChart className="h-4 w-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>

              {report && (
                <Button
                  variant="outline"
                  onClick={exportReport}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              )}
            </div>

            {error && (
              <Alert variant="destructive">
                {error}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card>
            <CardContent className="py-8">
              <LoadingState />
              <p className="text-center text-muted-foreground mt-4">
                Analyzing aspiration data and generating recommendations...
              </p>
            </CardContent>
          </Card>
        )}

        {/* Report Display */}
        {report && !loading && (
          <>
            {/* Summary Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {renderProgressCard()}
              {renderAnalysisCard()}
            </div>

            {/* Detailed Report Tabs */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex space-x-4 border-b">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                      activeTab === 'overview'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <FileText className="h-4 w-4 inline mr-2" />
                    Overview
                  </button>
                  <button
                    onClick={() => setActiveTab('recommendations')}
                    className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                      activeTab === 'recommendations'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Target className="h-4 w-4 inline mr-2" />
                    Recommendations
                  </button>
                  <button
                    onClick={() => setActiveTab('goals')}
                    className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                      activeTab === 'goals'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <BarChart className="h-4 w-4 inline mr-2" />
                    Goals Analysis
                  </button>
                  <button
                    onClick={() => setActiveTab('insights')}
                    className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                      activeTab === 'insights'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Lightbulb className="h-4 w-4 inline mr-2" />
                    Insights
                  </button>
                </div>

                <div className="mt-6">
                    {activeTab === 'overview' && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Report Summary</h3>
                        <p className="text-muted-foreground">
                          Generated on {new Date(report.generatedAt).toLocaleString()}
                        </p>

                        {/* Key metrics summary */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <Card>
                            <CardContent className="pt-4">
                              <div className="text-2xl font-bold">{report.summary.overallProgress}%</div>
                              <p className="text-xs text-muted-foreground">Overall Progress</p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="pt-4">
                              <div className="text-2xl font-bold">{report.summary.completedGoals}</div>
                              <p className="text-xs text-muted-foreground">Goals Complete</p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="pt-4">
                              <div className="text-2xl font-bold">{report.summary.completedItems}</div>
                              <p className="text-xs text-muted-foreground">Tasks Done</p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="pt-4">
                              <div className="text-2xl font-bold">{report.summary.estimatedCompletion}</div>
                              <p className="text-xs text-muted-foreground">Est. Time</p>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    )}

                    {activeTab === 'recommendations' && renderRecommendations()}

                    {activeTab === 'goals' && renderGoalsAnalysis()}

                    {activeTab === 'insights' && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Key Insights</h3>
                        <div className="space-y-2">
                          {report.insights?.map((insight, idx) => (
                            <Card key={idx}>
                              <CardContent className="pt-4">
                                <p className="text-sm flex items-start gap-2">
                                  <Lightbulb className="h-4 w-4 mt-0.5 text-yellow-600" />
                                  <span>{insight}</span>
                                </p>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Container>
  );
}